import { Box, Link, Stack } from '@mui/material';
import { Button } from 'system/atoms';
import { SummaryField } from 'common/ui/SummaryField';
import {
  GetCandidateBatchQuery,
  useInitializeCandidateBatchMutation,
  useInitializeDraftCandidateBatchMutation
} from 'generated-graphql/hooks/apollo-hooks';
import {
  AutomationTaskInput,
  CandidateBatchStatus
} from 'generated-graphql/types';
import { useGetCampaignNames } from 'modules/analyse/hooks/useGetCampaignNames';
import { AnalysisRequirementInput } from 'modules/analyse/types/AnalysisRequirementInput';
import { mapToSummaryAnalyse } from 'modules/analyse/utils/mapToSummaryAnalyse';
import { useNavigate } from 'react-router-dom';
import { initializeCandidateBatchGroup } from 'services/candidate/api';
import { processCandidateBatches } from 'services/candidate/api';
import { AtsJobInput } from 'services/candidate/types';
import { useLoadingOverlay } from 'services/loading/hooks';
import { Paths } from 'services/navigation/paths';
import { ToastType, useToast } from 'services/toast';

type Props = {
  atsJob: AtsJobInput;
  candidateBatch: GetCandidateBatchQuery['getCandidateBatch'];
  candidateBatchGroupName: string | undefined;
  requirements: AnalysisRequirementInput[];
  automationTasks: AutomationTaskInput[];
  onBack: () => void;
};

export const ATSRequirementsSummaryStep: React.FC<Props> = ({
  atsJob,
  candidateBatch,
  candidateBatchGroupName,
  requirements,
  automationTasks,
  onBack
}) => {
  const { showLoadingOverlay, hideLoadingOverlay } = useLoadingOverlay();
  const { showToast } = useToast();
  const navigate = useNavigate();

  const [initializeCandidateBatchMutation] =
    useInitializeCandidateBatchMutation();

  const { campaignNames } = useGetCampaignNames({
    automationTasks: automationTasks || []
  });

  const summaryAnalyse = mapToSummaryAnalyse({
    campaignNames,
    rulesetChosenTitle: candidateBatchGroupName,
    requirements,
    automationTasks: automationTasks || []
  });

  const [initializeDraftCandidateBatchMutation] =
    useInitializeDraftCandidateBatchMutation();

  const initializeDraftCandidateBatch = async (candidateBatchId: string) => {
    showLoadingOverlay();

    try {
      await initializeDraftCandidateBatchMutation({
        variables: {
          input: {
            candidateBatchId,
            automationTasks,
            requirements
          }
        }
      });

      navigate(`${Paths.ANALYSE}/${candidateBatchId}`);

      hideLoadingOverlay();

      showToast({
        title: 'Analysis live',
        message: 'Any applicants will be screened automatically.',
        type: ToastType.Success
      });
    } catch (error) {
      showToast({
        title: 'Something went wrong',
        message: 'Failed to create analysis live analysis',
        type: ToastType.Error
      });
    } finally {
      hideLoadingOverlay();
    }
  };

  const handleSubmit = async () => {
    if (candidateBatch) {
      if (candidateBatch.status !== CandidateBatchStatus.Draft) {
        showToast({
          title: 'Analysis already live',
          message: 'This job already has a live analysis — taking you to it.',
          type: ToastType.Warning
        });
        navigate(`${Paths.ANALYSE}/${candidateBatch.id}`);
        return;
      }

      await initializeDraftCandidateBatch(candidateBatch.id);
      return;
    }

    showLoadingOverlay();

    try {
      if (candidateBatchGroupName) {
        const { id } = await initializeCandidateBatchGroup({
          name: candidateBatchGroupName,
          automationTasks,
          analysisRequirements: requirements
        });

        const firstCandidateBatchGroup = await processCandidateBatches({
          atsJobs: [atsJob],
          candidateBatchGroupId: id
        });

        const candidateBatchId =
          firstCandidateBatchGroup.candidateBatches.items[0].id;
        navigate(`${Paths.ANALYSE}/${candidateBatchId}`);
        showToast({
          message: 'Your analysis has been created',
          type: ToastType.Success
        });
        return;
      }

      const result = await initializeCandidateBatchMutation({
        variables: {
          input: {
            jobDescription: atsJob.jobDescription,
            requirements,
            atsJobId: atsJob.atsJobId,
            atsJobReference: atsJob.atsJobReference,
            automationTasks,
            atsQuestions: atsJob.atsQuestions,
            atsJobPostingUrl: atsJob.atsJobPostingUrl
          }
        }
      });

      const id = result?.data?.initializeCandidateBatch?.id;

      if (!id) {
        showToast({
          message: 'Something went wrong',
          type: ToastType.Error
        });
        return;
      }

      showToast({
        message: 'Analysis created successfully',
        type: ToastType.Success
      });
      navigate(`${Paths.ANALYSE}/${id}`);
    } catch {
      showToast({
        title: 'Something went wrong',
        message: 'We could not create this analysis. Please try again.',
        type: ToastType.Error
      });
    } finally {
      hideLoadingOverlay();
    }
  };

  const orderedFields = [
    'ruleset',
    'mandatoryRequirements',
    'optionalRequirements',
    'rules',
    'actions'
  ] as const;

  const renderSummaryItem = (
    fieldKey: string,
    field: { label: string; value: string[] | string },
    showEditLink = true
  ) => (
    <Stack
      key={fieldKey}
      direction="column"
      gap={fieldKey === 'mandatoryRequirements' ? '10px' : '20px'}
      alignItems="start"
    >
      <SummaryField label={field.label} value={field.value} />
      {showEditLink && (
        <Link
          onClick={onBack}
          underline="none"
          fontWeight="bold"
          sx={{ cursor: 'pointer' }}
        >
          Edit
        </Link>
      )}
    </Stack>
  );

  return (
    <Stack direction="column" gap="40px" alignItems="start">
      {orderedFields.map((fieldKey) => {
        const field = summaryAnalyse[fieldKey as keyof typeof summaryAnalyse];
        if (!field) return null;

        // Special case for mandatoryRequirements - only show edit link on optionalRequirements
        if (fieldKey === 'mandatoryRequirements') {
          return renderSummaryItem(fieldKey, field, false);
        }

        return renderSummaryItem(fieldKey, field, true);
      })}

      <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
        <Button variant="primary" onClick={handleSubmit}>
          Launch analysis
        </Button>
      </Box>
    </Stack>
  );
};
